import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox, DropdownMenu, Input, InputDecorator, Skeleton } from '@common/components';
import { SvgChevrondown, SvgRefresh } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import CountUp from 'react-countup';
import { TERM_CATEGORY, UnauthQueryOptions } from '@/api';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSubscribedState } from '@/hooks/useSubscribedState';
import { useGlobalStore } from '@/stores/GlobalStore';
import { DEFAULT_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from './constants';
interface PrefillFormProps {
  institutionId: number;
  defaultDonationBusinessId?: number;
  onSubmit: (data: {
    amount: number;
    purpose: string;
  }) => void;
}
export const PrefillForm = t0 => {
  const $ = _c(147);
  const {
    institutionId,
    defaultDonationBusinessId,
    onSubmit
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      welloTerms: false,
      privacyCollection: false,
      thirdPartyInfo: false,
      membershipTerms: false,
      addressVerification: false,
      shippingInfo: false,
      voluntaryDonation: false,
      refundPolicy: false,
      giftInfo: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [agreements, setAgreements] = useState(t1);
  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(false);
  const setTermsDrawerType = useGlobalStore(_temp);
  let t2;
  if ($[1] !== setTermsDrawerType) {
    t2 = termType => {
      setTermsDrawerType(termType);
    };
    $[1] = setTermsDrawerType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTermsView = t2;
  const requiredAgreements = ["welloTerms", "privacyCollection", "thirdPartyInfo", "membershipTerms", "addressVerification", "shippingInfo", "voluntaryDonation", "refundPolicy", "giftInfo"] as const;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["membershipTerms", "addressVerification", "shippingInfo"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const thirdPartySubItems = t3 as const;
  const isAgreementAll = requiredAgreements.every(key => agreements[key]);
  const isThirdPartyAllChecked = thirdPartySubItems.every(key_0 => agreements[key_0]);
  let t4;
  if ($[4] !== isAgreementAll) {
    t4 = () => {
      const newValue = !isAgreementAll;
      setAgreements({
        welloTerms: newValue,
        privacyCollection: newValue,
        thirdPartyInfo: newValue,
        membershipTerms: newValue,
        addressVerification: newValue,
        shippingInfo: newValue,
        voluntaryDonation: newValue,
        refundPolicy: newValue,
        giftInfo: newValue
      });
    };
    $[4] = isAgreementAll;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toggleAllAgreements = t4;
  let t5;
  if ($[6] !== agreements) {
    t5 = (key_1, checked) => {
      const newAgreements = {
        ...agreements,
        [key_1]: checked
      };
      if (key_1 === "thirdPartyInfo") {
        thirdPartySubItems.forEach(subKey => {
          newAgreements[subKey] = checked;
        });
      }
      if (thirdPartySubItems.includes(key_1 as (typeof thirdPartySubItems)[number])) {
        const allSubItemsChecked = thirdPartySubItems.every(subKey_0 => subKey_0 === key_1 ? checked : newAgreements[subKey_0]);
        newAgreements.thirdPartyInfo = allSubItemsChecked;
      }
      setAgreements(newAgreements);
    };
    $[6] = agreements;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const updateAgreement = t5;
  let t6;
  if ($[8] !== isThirdPartyExpanded) {
    t6 = () => {
      setIsThirdPartyExpanded(!isThirdPartyExpanded);
    };
    $[8] = isThirdPartyExpanded;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const toggleThirdPartyAccordion = t6;
  const [amount, setAmount] = useState(DEFAULT_DONATION_AMOUNT);
  let t7;
  if ($[10] !== institutionId) {
    t7 = UnauthQueryOptions["get:/wello/v2/allows/home/donation-business/institution/{institutionId}"]({
      path: {
        institutionId
      }
    });
    $[10] = institutionId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = {
      ...t7,
      select: _temp2
    };
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data: donationBusinessPurposes
  } = useQuery(t8);
  let t9;
  if ($[14] !== defaultDonationBusinessId || $[15] !== donationBusinessPurposes) {
    t9 = donationBusinessPurposes && donationBusinessPurposes.length > 0 ? (donationBusinessPurposes.find(businessPurpose => businessPurpose.donationBusinessId === defaultDonationBusinessId) ?? donationBusinessPurposes[0]).donationBusinessName : "";
    $[14] = defaultDonationBusinessId;
    $[15] = donationBusinessPurposes;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const defaultPurpose = t9;
  const [purpose, setPurpose] = useSubscribedState(defaultPurpose);
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = value => {
      const sanitizedValue = value.replaceAll(/[^0-9]/g, "");
      if (!/^[\d,]*$/.test(sanitizedValue)) {
        return;
      }
      setAmount(Number(sanitizedValue));
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleAmountChange = t10;
  let t11;
  if ($[18] !== donationBusinessPurposes?.[0]?.donationBusinessName || $[19] !== setPurpose) {
    t11 = () => {
      setAmount(DEFAULT_DONATION_AMOUNT);
      setPurpose(donationBusinessPurposes?.[0]?.donationBusinessName ?? "");
      setAgreements({
        welloTerms: false,
        privacyCollection: false,
        thirdPartyInfo: false,
        membershipTerms: false,
        addressVerification: false,
        shippingInfo: false,
        voluntaryDonation: false,
        refundPolicy: false,
        giftInfo: false
      });
    };
    $[18] = donationBusinessPurposes?.[0]?.donationBusinessName;
    $[19] = setPurpose;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleReset = t11;
  const t12 = isDesktopViewport ? "H56" : "H48";
  let t13;
  if ($[21] !== amount) {
    t13 = amount.toLocaleString();
    $[21] = amount;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => handleAmountChange(e.target.value);
    t15 = <span className="font-400 text-Gray-900 typo-body3">원</span>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t13) {
    t16 = <Input dimensions={t12} placeholder="0" inputMode="numeric" value={t13} onChange={t14} rightContent={t15} maxLength={16} className="mb-24" />;
    $[25] = t12;
    $[26] = t13;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== donationBusinessPurposes || $[29] !== isDesktopViewport || $[30] !== purpose || $[31] !== setPurpose) {
    t17 = <InputDecorator label={"\uAE30\uBD80\uC6A9\uB3C4 \uC120\uD0DD"}>{donationBusinessPurposes ? <DropdownMenu dimensions={isDesktopViewport ? "H56" : "H48"} options={donationBusinessPurposes.map(_temp3)} value={purpose} onChange={setPurpose} placeholder={"\uAE30\uBD80\uC6A9\uB3C4\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694"} className="mb-24 w-full" /> : <Skeleton />}</InputDecorator>;
    $[28] = donationBusinessPurposes;
    $[29] = isDesktopViewport;
    $[30] = purpose;
    $[31] = setPurpose;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className={cn("flex gap-8 font-500 text-Gray-800 typo-body3", "mobile-tablet:typo-body4")}>기부 금액</span>;
    t19 = cn("font-700 typo-h6", "mobile-tablet:typo-body1");
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  let t20;
  if ($[35] !== amount) {
    t20 = <div className="mb-20 flex items-center justify-between border-y-1 border-Gray-300 py-20">{t18}<span className={t19}><CountUp preserveValue={true} end={amount} duration={0.3} />원</span></div>;
    $[35] = amount;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== amount) {
    t21 = amount < MIN_DONATION_AMOUNT && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }} exit={{
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }} className="flex flex-col gap-8"><p className="text-center font-400 text-R-500 typo-body4">{MIN_DONATION_AMOUNT.toLocaleString()}원 이상부터 기부 가능해요.</p></motion.div>;
    $[37] = amount;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t21) {
    t22 = <AnimatePresence>{t21}</AnimatePresence>;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className={cn("mb-16 text-start text-Gray-700 typo-body3", "mobile-tablet:mb-16 mobile-tablet:typo-body4")}>이용약관에 동의해주세요.</p>;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== isAgreementAll || $[43] !== toggleAllAgreements) {
    t24 = <div className="mb-16 rounded-xsmall bg-Gray-100 px-12 py-8"><Checkbox className="font-600" checked={isAgreementAll} onCheckedChange={toggleAllAgreements}>전체 동의</Checkbox></div>;
    $[42] = isAgreementAll;
    $[43] = toggleAllAgreements;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== updateAgreement) {
    t25 = checked_0 => updateAgreement("welloTerms", checked_0);
    $[45] = updateAgreement;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="flex items-start gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">웰로 고향사랑기부제 이용약관</span></div>;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== handleTermsView) {
    t27 = <div className="flex flex-1 items-start justify-between gap-4">{t26}<button className="font-600 underline typo-body4" type="button" onClick={e_0 => {
        e_0.stopPropagation();
        handleTermsView(TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS);
      }}>보기</button></div>;
    $[48] = handleTermsView;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== agreements.welloTerms || $[51] !== t25 || $[52] !== t27) {
    t28 = <Checkbox checked={agreements.welloTerms} onCheckedChange={t25} className="justify-between">{t27}</Checkbox>;
    $[50] = agreements.welloTerms;
    $[51] = t25;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== updateAgreement) {
    t29 = checked_1 => updateAgreement("privacyCollection", checked_1);
    $[54] = updateAgreement;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="flex items-start gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">개인정보 수집이용 동의</span></div>;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== handleTermsView) {
    t31 = <div className="flex flex-1 items-start justify-between gap-4">{t30}<button className="font-600 underline typo-body4" type="button" onClick={e_1 => {
        e_1.stopPropagation();
        handleTermsView(TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT);
      }}>보기</button></div>;
    $[57] = handleTermsView;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== agreements.privacyCollection || $[60] !== t29 || $[61] !== t31) {
    t32 = <Checkbox checked={agreements.privacyCollection} onCheckedChange={t29} className="justify-between">{t31}</Checkbox>;
    $[59] = agreements.privacyCollection;
    $[60] = t29;
    $[61] = t31;
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] !== updateAgreement) {
    t33 = checked_2 => updateAgreement("thirdPartyInfo", checked_2);
    $[63] = updateAgreement;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span className="font-500 text-Gray-black typo-body4">(필수)</span>;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== toggleThirdPartyAccordion) {
    t35 = e_2 => {
      e_2.preventDefault();
      toggleThirdPartyAccordion();
    };
    $[66] = toggleThirdPartyAccordion;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-start">고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공 동의</span>;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== isThirdPartyExpanded) {
    t37 = cn("size-16 min-h-21 min-w-16 transition-transform duration-200", {
      "rotate-180": isThirdPartyExpanded
    });
    $[69] = isThirdPartyExpanded;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t37) {
    t38 = <SvgChevrondown className={t37} />;
    $[71] = t37;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] !== t35 || $[74] !== t38) {
    t39 = <div className="flex flex-1 items-start justify-between gap-4"><div className="flex items-start gap-4">{t34}<span className="flex cursor-pointer items-start gap-4 font-400 text-Gray-800 typo-body4" onClick={t35}>{t36}{t38}</span></div></div>;
    $[73] = t35;
    $[74] = t38;
    $[75] = t39;
  } else {
    t39 = $[75];
  }
  let t40;
  if ($[76] !== isThirdPartyAllChecked || $[77] !== t33 || $[78] !== t39) {
    t40 = <Checkbox checked={isThirdPartyAllChecked} onCheckedChange={t33} className="justify-between">{t39}</Checkbox>;
    $[76] = isThirdPartyAllChecked;
    $[77] = t33;
    $[78] = t39;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== agreements.addressVerification || $[81] !== agreements.membershipTerms || $[82] !== agreements.shippingInfo || $[83] !== handleTermsView || $[84] !== isThirdPartyExpanded || $[85] !== updateAgreement) {
    t41 = isThirdPartyExpanded && <div className="flex flex-col pl-16"><Checkbox checked={agreements.membershipTerms} onCheckedChange={checked_3 => updateAgreement("membershipTerms", checked_3)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">고향사랑e음 회원가입을 위한 개인정보 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_3 => {
            e_3.stopPropagation();
            handleTermsView(TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT);
          }}>보기</button></div></Checkbox><Checkbox checked={agreements.addressVerification} onCheckedChange={checked_4 => updateAgreement("addressVerification", checked_4)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">주소지 확인을 위한 주민등록번호 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_4 => {
            e_4.stopPropagation();
            handleTermsView(TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT);
          }}>보기</button></div></Checkbox><Checkbox checked={agreements.shippingInfo} onCheckedChange={checked_5 => updateAgreement("shippingInfo", checked_5)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">답례품 배송 관련 개인정보 제3자 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_5 => {
            e_5.stopPropagation();
            handleTermsView(TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT);
          }}>보기</button></div></Checkbox></div>;
    $[80] = agreements.addressVerification;
    $[81] = agreements.membershipTerms;
    $[82] = agreements.shippingInfo;
    $[83] = handleTermsView;
    $[84] = isThirdPartyExpanded;
    $[85] = updateAgreement;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] !== t40 || $[88] !== t41) {
    t42 = <div className="flex flex-col gap-8">{t40}{t41}</div>;
    $[87] = t40;
    $[88] = t41;
    $[89] = t42;
  } else {
    t42 = $[89];
  }
  let t43;
  if ($[90] !== updateAgreement) {
    t43 = checked_6 => updateAgreement("voluntaryDonation", checked_6);
    $[90] = updateAgreement;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">공무 관련 강요 없이 자발적으로 기부합니다.</span></span>;
    $[92] = t44;
  } else {
    t44 = $[92];
  }
  let t45;
  if ($[93] !== handleTermsView) {
    t45 = <div className="flex flex-1 items-start justify-between gap-4">{t44}<button className="font-600 underline typo-body4" type="button" onClick={e_6 => {
        e_6.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE);
      }}>보기</button></div>;
    $[93] = handleTermsView;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] !== agreements.voluntaryDonation || $[96] !== t43 || $[97] !== t45) {
    t46 = <Checkbox checked={agreements.voluntaryDonation} onCheckedChange={t43} className="justify-between">{t45}</Checkbox>;
    $[95] = agreements.voluntaryDonation;
    $[96] = t43;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== updateAgreement) {
    t47 = checked_7 => updateAgreement("refundPolicy", checked_7);
    $[99] = updateAgreement;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">안내사항(환불 불가·세액공제 등)을 확인했습니다.</span></span>;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  let t49;
  if ($[102] !== handleTermsView) {
    t49 = <div className="flex flex-1 items-start justify-between gap-4">{t48}<button className="font-600 underline typo-body4" type="button" onClick={e_7 => {
        e_7.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GUIDANCE_NOTICE);
      }}>보기</button></div>;
    $[102] = handleTermsView;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  let t50;
  if ($[104] !== agreements.refundPolicy || $[105] !== t47 || $[106] !== t49) {
    t50 = <Checkbox checked={agreements.refundPolicy} onCheckedChange={t47} className="justify-between">{t49}</Checkbox>;
    $[104] = agreements.refundPolicy;
    $[105] = t47;
    $[106] = t49;
    $[107] = t50;
  } else {
    t50 = $[107];
  }
  let t51;
  if ($[108] !== updateAgreement) {
    t51 = checked_8 => updateAgreement("giftInfo", checked_8);
    $[108] = updateAgreement;
    $[109] = t51;
  } else {
    t51 = $[109];
  }
  let t52;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">답례품 정보 확인 및 동의</span></span>;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] !== handleTermsView) {
    t53 = <div className="flex flex-1 items-start justify-between gap-4">{t52}<button className="font-600 underline typo-body4" type="button" onClick={e_8 => {
        e_8.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT);
      }}>보기</button></div>;
    $[111] = handleTermsView;
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  let t54;
  if ($[113] !== agreements.giftInfo || $[114] !== t51 || $[115] !== t53) {
    t54 = <Checkbox checked={agreements.giftInfo} onCheckedChange={t51} className="justify-between">{t53}</Checkbox>;
    $[113] = agreements.giftInfo;
    $[114] = t51;
    $[115] = t53;
    $[116] = t54;
  } else {
    t54 = $[116];
  }
  let t55;
  if ($[117] !== t28 || $[118] !== t32 || $[119] !== t42 || $[120] !== t46 || $[121] !== t50 || $[122] !== t54) {
    t55 = <div className="mb-32 flex flex-col">{t28}{t32}{t42}{t46}{t50}{t54}</div>;
    $[117] = t28;
    $[118] = t32;
    $[119] = t42;
    $[120] = t46;
    $[121] = t50;
    $[122] = t54;
    $[123] = t55;
  } else {
    t55 = $[123];
  }
  let t56;
  if ($[124] !== t20 || $[125] !== t22 || $[126] !== t24 || $[127] !== t55) {
    t56 = <div className="flex flex-col">{t20}{t22}{t23}{t24}{t55}</div>;
    $[124] = t20;
    $[125] = t22;
    $[126] = t24;
    $[127] = t55;
    $[128] = t56;
  } else {
    t56 = $[128];
  }
  let t57;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <SvgRefresh className="size-16" />;
    $[129] = t57;
  } else {
    t57 = $[129];
  }
  let t58;
  if ($[130] !== handleReset) {
    t58 = <Button theme="sub-line" onClick={handleReset}>{t57}초기화</Button>;
    $[130] = handleReset;
    $[131] = t58;
  } else {
    t58 = $[131];
  }
  const t59 = !isAgreementAll || !amount || !purpose || amount < MIN_DONATION_AMOUNT;
  let t60;
  if ($[132] !== amount || $[133] !== onSubmit || $[134] !== purpose) {
    t60 = () => onSubmit({
      amount,
      purpose
    });
    $[132] = amount;
    $[133] = onSubmit;
    $[134] = purpose;
    $[135] = t60;
  } else {
    t60 = $[135];
  }
  let t61;
  if ($[136] !== t59 || $[137] !== t60) {
    t61 = <Button disabled={t59} onClick={t60} className="grow">기부하기</Button>;
    $[136] = t59;
    $[137] = t60;
    $[138] = t61;
  } else {
    t61 = $[138];
  }
  let t62;
  if ($[139] !== t58 || $[140] !== t61) {
    t62 = <footer className="flex gap-8">{t58}{t61}</footer>;
    $[139] = t58;
    $[140] = t61;
    $[141] = t62;
  } else {
    t62 = $[141];
  }
  let t63;
  if ($[142] !== t16 || $[143] !== t17 || $[144] !== t56 || $[145] !== t62) {
    t63 = <div>{t16}{t17}{t56}{t62}</div>;
    $[142] = t16;
    $[143] = t17;
    $[144] = t56;
    $[145] = t62;
    $[146] = t63;
  } else {
    t63 = $[146];
  }
  return t63;
};
function _temp(state) {
  return state.setTermsDrawerType;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context?.contents;
}
function _temp3(t0) {
  const {
    donationBusinessName: donation_business_name
  } = t0;
  return {
    label: donation_business_name,
    value: donation_business_name
  };
}